import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from "react";
import classNames from "classnames";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { UiUploadIcon } from "@jti/brand-icons/ui-upload";
import { Button } from "../../../components/Button";
import { InputWrapper } from "../../../components/Inputs/components/InputWrapper";
import { Typography } from "../../../components/Typography";
import { useTranslations } from "../../../providers/useTranslations";
import styles from "./FilePicker.module.scss";
const formatFileSize = (bytes) => {
    if (bytes === 0)
        return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
export const FilePicker = ({ id = "filePicker", label = "Have something to upload?", helperText = "Supported formats: JPEG, PNG, PDF, DOCX. Max size: XXX MB.", errorText = "Please upload a valid file.", isError = false, isHelperTextVisible = true, isOptional = true, maxFiles = 3, maxSize = 5 * 1024 * 1024, // 5MB default
acceptedFileTypes = ["image/jpeg", "image/png", "application/pdf", "application/msword"], buttonLabel = "Browse files", buttonColor = "secondary", buttonSize = "large", buttonTheme = "light", containerClassName = "", wrapperClassName = "", dataTestId = "filePicker", showRequiredAsterisk = false, theme = "light", value = [], onChange, }) => {
    const { __ } = useTranslations();
    const [fileError, setFileError] = useState(undefined);
    const fileInputRef = useRef(null);
    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        let errorMsg = undefined;
        const validFiles = selectedFiles.filter((file) => {
            if (!acceptedFileTypes.includes(file.type)) {
                errorMsg = __("filePicker.error.unsupportedType") || "Unsupported file type.";
                return false;
            }
            if (file.size > maxSize) {
                errorMsg = __("filePicker.error.maxSize") || "File size exceeds the maximum allowed.";
                return false;
            }
            return true;
        });
        if (value.length + validFiles.length > maxFiles) {
            errorMsg =
                __("filePicker.error.maxFilesReached") || "Maximum number of allowable file uploads have been exceeded.";
            setFileError(errorMsg);
            return;
        }
        const newFiles = [...value, ...validFiles];
        setFileError(errorMsg);
        onChange?.(newFiles);
    };
    const handleRemoveFile = (index) => {
        const newFiles = value.filter((_, i) => i !== index);
        setFileError(undefined);
        onChange?.(newFiles);
    };
    const handleClick = () => {
        fileInputRef.current?.click();
    };
    const displayErrorText = isError && !!errorText ? errorText : fileError;
    const hasError = (isError && !!errorText) || !!fileError;
    return (_jsxs(_Fragment, { children: [_jsx(InputWrapper, { htmlFor: id, label: label, errorText: displayErrorText, helperText: helperText, isError: hasError, isHelperTextVisible: isHelperTextVisible, isOptional: isOptional, containerClassName: classNames(containerClassName, styles.filePickerContainer), wrapperClassName: classNames(wrapperClassName, styles.filePickerWrapper), dataTestId: `${dataTestId}-container`, showRequiredAsterisk: showRequiredAsterisk, theme: theme, children: _jsxs("div", { className: styles.inputContainer, children: [_jsx("input", { type: "file", ref: fileInputRef, onChange: handleFileChange, accept: acceptedFileTypes.join(","), multiple: maxFiles > 1, hidden: true, name: id, id: id, "data-testid": dataTestId }), _jsxs(Button, { className: styles.uploadButton, color: buttonColor, size: buttonSize, theme: buttonTheme, onClick: handleClick, disabled: value.length >= maxFiles, children: [buttonLabel, _jsx(UiUploadIcon, {})] })] }) }), value.length > 0 && (_jsx("div", { className: styles.fileList, "data-testid": "fileList", children: value.map((file, index) => (_jsxs("div", { className: styles.fileItem, children: [_jsxs("div", { className: styles.fileInfo, children: [_jsx(Typography, { variant: "body1", className: styles.fileName, children: file.name }), _jsxs(Typography, { variant: "body1", className: styles.fileSize, children: ["(", formatFileSize(file.size), ")"] })] }), _jsx("button", { type: "button", className: styles.removeButton, onClick: () => handleRemoveFile(index), children: _jsx(UiCrossIcon, { width: 22, height: 22 }) })] }, file.name))) }))] }));
};
//# sourceMappingURL=FilePicker.js.map